import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, FileText, RefreshCcw } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../Config/Config';

const ComplianceLayout = ({ title, icon: Icon, children }) => (
  <div className="font-sans selection:bg-blue-200 bg-slate-50 min-h-screen pb-24">
    <Helmet>
      <title>{title} | WESTBERG Institute</title>
      <meta name="description" content={`${title} for WESTBERG Institute`} />
    </Helmet>

    {/* Header */}
    <div className="bg-slate-900 pt-24 pb-32 px-6 text-center relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 opacity-20 pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[80%] rounded-full bg-blue-500 blur-[150px] md:mix-blend-screen md:animate-pulse"></div>
      </div>
      <div className="relative z-10 max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom-8 duration-700">
        <div className="w-16 h-16 bg-blue-500/20 text-blue-400 rounded-[1.2rem] flex items-center justify-center mx-auto mb-6 border border-blue-400/30 backdrop-blur-sm">
          <Icon size={32} />
        </div>
        <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight">{title}</h1>
        <p className="text-blue-300 font-bold uppercase tracking-widest text-sm">Legal & Compliance</p>
      </div>
    </div>

    {/* Content */}
    <div className="max-w-4xl mx-auto px-6 -mt-20 relative z-20">
      <div className="bg-white/90 backdrop-blur-xl rounded-[2.5rem] p-8 md:p-16 border border-white shadow-xl shadow-slate-200/50 animate-in fade-in slide-in-from-bottom-12 duration-700 delay-100">
        <div className="prose prose-slate prose-lg max-w-none prose-headings:font-extrabold prose-headings:text-slate-900 prose-p:text-slate-600 prose-p:leading-relaxed prose-a:text-blue-600 hover:prose-a:text-blue-700">
          {children}
        </div>
      </div>
    </div>
  </div>
);

const useLegalSettings = () => {
  const [settings, setSettings] = useState({ privacyPolicy: '', termsConditions: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const docSnap = await getDoc(doc(db, 'settings', 'legal'));
        if (docSnap.exists()) {
          setSettings(docSnap.data());
        }
      } catch (err) {
        console.error('Error fetching legal settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  return { settings, loading };
};

const PrivacyPolicy = () => {
  const { settings, loading } = useLegalSettings();
  
  return (
    <ComplianceLayout title="Privacy Policy" icon={Shield}>
      {loading ? (
        <p className="text-slate-400 animate-pulse">Loading...</p>
      ) : settings.privacyPolicy ? (
        <div dangerouslySetInnerHTML={{ __html: settings.privacyPolicy }} />
      ) : (
        <>
          <p className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-10">Last updated: June 2026</p>
          <h2>Information We Collect</h2>
          <p>We collect information you provide directly to us when you register for a course, fill out a form, or communicate with us.</p>
          <h2>How We Use Your Information</h2>
          <p>We use the information we collect to provide, maintain, and improve our services, and to communicate with you.</p>
        </>
      )}
    </ComplianceLayout>
  );
};

const TermsAndConditions = () => {
  const { settings, loading } = useLegalSettings();

  return (
    <ComplianceLayout title="Terms and Conditions" icon={FileText}>
      {loading ? (
        <p className="text-slate-400 animate-pulse">Loading...</p>
      ) : settings.termsConditions ? (
        <div dangerouslySetInnerHTML={{ __html: settings.termsConditions }} />
      ) : (
        <>
          <p className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-10">Last updated: June 2026</p>
          <h2>Acceptance of Terms</h2>
          <p>By accessing and using our services, you agree to be bound by these Terms and Conditions.</p>
          <h2>User Conduct</h2>
          <p>You agree to use our services only for lawful purposes and in accordance with these Terms.</p>
        </>
      )}
    </ComplianceLayout>
  );
};

const RefundPolicy = () => (
  <ComplianceLayout title="Refund Policy" icon={RefreshCcw}>
    <p className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-10">Last updated: June 2026</p>
    <h2>Course Fees</h2>
    <p>Course fees are non-refundable once the batch commences, except under specific circumstances outlined below.</p>
    <h2>Cancellation</h2>
    <p>If you cancel your enrollment at least 15 days before the batch start date, you are eligible for a 90% refund.</p>
  </ComplianceLayout>
);

export { PrivacyPolicy, TermsAndConditions, RefundPolicy };
